import { useEffect, useMemo, useState } from 'react';
import { Table, Button, Spinner, Alert, Badge, Modal, Form } from 'react-bootstrap';
import { apiGet, apiPost, apiPut } from '../services/apiClient';

const statusVariant = {
  connected: 'success',
  disconnected: 'secondary',
  error: 'danger',
};

const authTypes = ['none', 'api_key', 'oauth2', 'basic'];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ConnectionModal = ({ show, onHide, onSave, providers, initial }) => {
  const [form, setForm] = useState(() => ({
    provider_id: initial?.provider?.id || '',
    label: initial?.label || '',
    auth_type: initial?.auth_type || 'none',
    status: initial?.status || 'disconnected',
    rate_limit_per_min: initial?.rate_limit_per_min || 60,
    credentials: '',
    webhook_secret: '',
  }));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      provider_id: initial?.provider?.id || '',
      label: initial?.label || '',
      auth_type: initial?.auth_type || 'none',
      status: initial?.status || 'disconnected',
      rate_limit_per_min: initial?.rate_limit_per_min || 60,
      credentials: '',
      webhook_secret: '',
    });
    setError('');
  }, [initial, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.label) {
      setError('Label is required');
      return;
    }
    if (!form.provider_id) {
      setError('Provider is required');
      return;
    }
    let credentialsPayload;
    if (form.credentials.trim()) {
      try {
        credentialsPayload = JSON.parse(form.credentials);
      } catch (err) {
        setError('Credentials must be valid JSON');
        return;
      }
    }
    setSaving(true);
    try {
      await onSave({
        provider_id: form.provider_id,
        label: form.label,
        auth_type: form.auth_type,
        status: form.status,
        rate_limit_per_min: Number(form.rate_limit_per_min || 0),
        credentials: credentialsPayload,
        webhook_secret: form.webhook_secret || undefined,
      });
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const providerHint = useMemo(() => {
    if (!form.provider_id) return null;
    const provider = providers.find((p) => p.id === form.provider_id);
    if (!provider) return null;
    if (provider.key === 'ical') {
      return '{"ical_import_url":"https://example.com/calendar.ics","ical_export_url":""}';
    }
    return '{"api_base_url":"https://api.partner.com","api_key":"..."}';
  }, [form.provider_id, providers]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{initial ? 'Edit Connection' : 'Add Connection'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Provider</Form.Label>
            <Form.Select
              value={form.provider_id}
              onChange={(e) => setForm((prev) => ({ ...prev, provider_id: e.target.value }))}
              disabled={!!initial}
              required
            >
              <option value="">Select provider</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name} ({provider.key})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Label</Form.Label>
            <Form.Control
              value={form.label}
              onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              placeholder="Homevilas Main Account"
              required
            />
          </Form.Group>
          <div className="row g-2">
            <Form.Group className="col-md-4">
              <Form.Label>Auth type</Form.Label>
              <Form.Select
                value={form.auth_type}
                onChange={(e) => setForm((prev) => ({ ...prev, auth_type: e.target.value }))}
              >
                {authTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="connected">connected</option>
                <option value="disconnected">disconnected</option>
                <option value="error">error</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4">
              <Form.Label>Rate limit / min</Form.Label>
              <Form.Control
                type="number"
                value={form.rate_limit_per_min}
                onChange={(e) => setForm((prev) => ({ ...prev, rate_limit_per_min: e.target.value }))}
                min={1}
              />
            </Form.Group>
          </div>
          <Form.Group className="mt-3">
            <Form.Label>Credentials (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={form.credentials}
              placeholder={providerHint || '{"api_base_url":"...","api_key":"..."}'}
              onChange={(e) => setForm((prev) => ({ ...prev, credentials: e.target.value }))}
            />
            <Form.Text className="text-muted">
              Leave blank to keep existing credentials. {providerHint ? `Example: ${providerHint}` : ''}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Webhook secret (optional)</Form.Label>
            <Form.Control
              value={form.webhook_secret}
              placeholder="Leave blank to keep existing"
              onChange={(e) => setForm((prev) => ({ ...prev, webhook_secret: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const ChannelConnectionsPage = ({ token }) => {
  const [connections, setConnections] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [connectionsRes, providersRes] = await Promise.all([
        apiGet('/api/admin/channel/connections', token),
        apiGet('/api/admin/channel/providers', token),
      ]);
      setConnections(connectionsRes || []);
      setProviders(providersRes || []);
    } catch (err) {
      setError(err.message || 'Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (payload) => {
    if (editing) {
      const body = { ...payload };
      if (body.credentials === undefined) delete body.credentials;
      if (body.webhook_secret === undefined) delete body.webhook_secret;
      await apiPut(`/api/admin/channel/connections/${editing.id}`, body, token);
    } else {
      await apiPost('/api/admin/channel/connections', payload, token);
    }
    await load();
  };

  const handleTest = async (connectionId) => {
    try {
      await apiPost(`/api/admin/channel/connections/${connectionId}/test`, {}, token);
      await load();
    } catch (err) {
      setError(err.message || 'Connection test failed');
    }
  };

  const handleDisable = async (connectionId) => {
    try {
      await apiPut(`/api/admin/channel/connections/${connectionId}`, { status: 'disconnected' }, token);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to disable connection');
    }
  };

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">API Management - Connections</h4>
          <small className="text-muted">Create and test partner connections.</small>
        </div>
        <Button
          variant="primary"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          + Add Connection
        </Button>
      </div>

      {loading && (
        <div className="py-4 text-center">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && (
        <Table bordered hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>Provider</th>
              <th>Label</th>
              <th>Status</th>
              <th>Last tested</th>
              <th>Rate limit</th>
              <th>Last error</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {connections.map((conn) => (
              <tr key={conn.id}>
                <td>
                  <div className="fw-semibold">{conn.provider?.name}</div>
                  <small className="text-muted">{conn.provider?.key}</small>
                </td>
                <td>{conn.label}</td>
                <td>
                  <Badge bg={statusVariant[conn.status] || 'secondary'}>{conn.status}</Badge>
                </td>
                <td>{formatDateTime(conn.last_tested_at)}</td>
                <td>{conn.rate_limit_per_min}/min</td>
                <td className="text-truncate" style={{ maxWidth: 180 }}>
                  {conn.last_error || '—'}
                </td>
                <td className="d-flex gap-2">
                  <Button size="sm" variant="outline-primary" href={`/admin/channel/connections/${conn.id}`}>
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => {
                      setEditing(conn);
                      setModalOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="outline-success" onClick={() => handleTest(conn.id)}>
                    Test
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDisable(conn.id)}>
                    Disable
                  </Button>
                </td>
              </tr>
            ))}
            {!connections.length && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No connections created yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <ConnectionModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        onSave={handleSave}
        providers={providers}
        initial={editing}
      />
    </div>
  );
};

export default ChannelConnectionsPage;
